import { useState } from 'react';
import { Formik, FormikErrors } from 'formik';
import css from './AdminContactForm.module.scss';
import { AdminContactFormProps, AdminContactFormType } from './AdminContactForm.types';
import clone from 'just-clone';
import { AccountType, Country } from '../../../../../API/Enums';
import { upsertTextingOptIn } from '../../../../../API/GoMoto';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import {
    useEnsureAppointment,
    useAppointmentUpdateAndPersist,
    OptInStatus,
    useTexting,
    TextOptInStatus,
    ContactPreferenceEnum,
} from '../../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization, useLocale, useLocalizationExtension } from '../../../../../context/Localization/localization';
import { Locales, LocalesFull } from '../../../../../context/Localization/localization.types';
import { CTAButton, GMContactPreferenceButton } from '../../../../../controls/ButtonBase';
import { GMFormCheckbox } from '../../../../../controls/Checkbox';
import { GMDialogYesNo } from '../../../../../controls/Dialog';
import { GMForm, GMErrorMessage, GMFormWrapper } from '../../../../../controls/Form';
import { GMGrid } from '../../../../../controls/Grid';
import { KeyboardLayouts } from '../../../../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { GMFormPhoneField, GMFormEmailField } from '../../../../../controls/TextField';
import { nameof } from '../../../../../helpers/nameof';
import { formatAsPhoneNumber } from '../../../../../helpers/Number';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import { LegalDocumentLinks } from '../../../../../controls/LegalDocumentLinks/LegalDocumentLinks';

/**
 * For the current appointment, the advisor can indicate what type of notifications
 * the customer would like to recieve and to what email and/or phone number the customer
 * would prefer to recieve the notifications to when storing the key.
 *
 * If the key being stored is for an existing appointment, then the advisor has the ability
 * to change how the customer will receive notifications.
 *
 * @param onProceed - {@link AdminContactFormProps.onProceed onProceed} description.
 */
export const AdminContactForm: GoMotoComponent<AdminContactFormProps> = ({
    onProceed,
}) => {
    const {
        dealer: {
            key: dealerKey,
            name: dealerName,
            primaryLanguage,
            alternateLanguage,
            customerTextingActive,
            textingPhoneUS,
            textingPhoneCA,
            country,
        },
    } = useKioskState();
    const {
        appointment,
        appointment: {
            firstName = '',
            lastName = '',
            businessName = '',
            email = '',
            pickupEmail = '',
            phone = '',
            pickupPhone = '',
            locale = primaryLanguage,
            contactPreference = undefined,
            pickupContactPreference = undefined,
            accountType = AccountType.individual,
            key = '',
        } = {},
        isLoading,
    } = useEnsureAppointment(true);
    const setAppointment = useAppointmentUpdateAndPersist(true);
    const Localization = useLocalization();
    const kioskLocale = useLocale();
    const { emailInvalid, formatRequired, phoneInvalid } = useLocalizationExtension();
    const [showOptIn, setShowOptIn] = useState(false);
    const [optInStatus, setOptInStatus] = useState<OptInStatus | undefined>(undefined);
    const { checkPhoneTextingStatus } = useTexting();

    const textingPhone = country === Country.Canada ? textingPhoneCA : textingPhoneUS;
    const customerName = accountType === AccountType.business ? businessName : (firstName + ' ' + lastName);
    const initialValues: AdminContactFormType = {
        pickupEmail: (pickupEmail && pickupEmail.trim() !== '' ? pickupEmail : email) ?? '',
        pickupPhone: (pickupPhone && pickupPhone.trim() !== '' ? pickupPhone : phone) ?? '',
        pickupContactPreference: pickupContactPreference ?? contactPreference,
        locale,
        key,
    };

    const environment: string | undefined = process.env.REACT_APP_GOMOTO_API_USERNAME;

    const [previousPhone, setPreviousPhone] = useState<string>(initialValues.pickupPhone!);

    return (
        <Formik
            initialValues={initialValues}
            validateOnBlur={false}
            onSubmit={(values) => {
                const canOptIn = customerTextingActive
                    && (values.pickupContactPreference === 'text' || values.pickupContactPreference === 'both')
                    && !!optInStatus
                    && optInStatus.status === TextOptInStatus.NotDecided;
                const newEmail: string = values.pickupEmail!.trim();
                const newPhone: string = values.pickupPhone!.trim();

                if (!showOptIn && !canOptIn) {
                    setAppointment({
                        ...appointment!,
                        ...values,
                        ...(!email && newEmail ? { email: newEmail } : undefined),
                        ...(!phone && newPhone ? { phone: newPhone } : undefined),
                        ...(!contactPreference && values.pickupContactPreference !== 'both' ? { contactPreference: values.pickupContactPreference ?? 'email' } : undefined),
                        pickupEmail: newEmail,
                        pickupPhone: newPhone,
                        pickupContactPreference: values.pickupContactPreference ?? 'email',
                    }, true, false, false, true, undefined, false, true, false, false, false, true)
                        .finally(() => {
                            onProceed();
                        });
                }
                else {
                    setShowOptIn(canOptIn);
                }
            }}
            validate={async (values) => {
                const newValues: AdminContactFormType = clone(values);
                const errors: FormikErrors<AdminContactFormType> = {};
                const phoneRequired: boolean = newValues.pickupContactPreference === ContactPreferenceEnum.Text || newValues.pickupContactPreference === ContactPreferenceEnum.Both;
                const emailRequired: boolean = !customerTextingActive
                    || newValues.pickupContactPreference === ContactPreferenceEnum.Email
                    || newValues.pickupContactPreference === ContactPreferenceEnum.Both;
                const phoneChanged: boolean = newValues.pickupPhone !== initialValues.pickupPhone;

                newValues.pickupPhone = newValues.pickupPhone?.trim() ?? '';
                newValues.pickupEmail = newValues.pickupEmail?.trim() ?? '';

                if (customerTextingActive) {
                    if (newValues.pickupPhone.length > 0 && newValues.pickupPhone.length !== 10) {
                        errors.pickupPhone = phoneInvalid;
                    }
                    else if (phoneRequired) {
                        if (newValues.pickupPhone.length === 0) {
                            errors.pickupPhone = formatRequired(Localization.Phone);
                        }
                        else if ((!phoneChanged && optInStatus === undefined) || newValues.pickupPhone !== previousPhone) {
                            const { data } = await checkPhoneTextingStatus(newValues.pickupPhone);

                            setOptInStatus(data);
                            setPreviousPhone(data.phone);

                            if (data.isDeliverable === false) {
                                errors.pickupPhone = Localization.ThisPhoneNumberCannotReceiveTextNotifications;
                            }
                            else if (data.status === TextOptInStatus.OptedOut) {
                                errors.pickupPhone = Localization.formatString(
                                    Localization.ThisPhoneNumberHasOptedOut,
                                    dealerName,
                                    customerName,
                                    formatAsPhoneNumber(textingPhone as string),
                                ) as string;
                            }
                        }
                        else if (optInStatus?.isDeliverable === false) {
                            errors.pickupPhone = Localization.ThisPhoneNumberCannotReceiveTextNotifications;
                        }
                        else if (optInStatus?.status === TextOptInStatus.OptedOut) {
                            errors.pickupPhone = Localization.formatString(
                                Localization.ThisPhoneNumberHasOptedOut,
                                dealerName,
                                customerName,
                                formatAsPhoneNumber(textingPhone as string),
                            ) as string;
                        }
                    }

                    if (!newValues.pickupContactPreference) {
                        errors.pickupContactPreference = formatRequired(Localization.PickupContactPreference);
                    }
                }

                if (emailRequired && newValues.pickupEmail.length === 0) {
                    errors.pickupEmail = formatRequired(Localization.Email);
                }
                else if (newValues.pickupEmail.length > 0 && !(/^[^@]+@[^@.]+\.[^@]+$/).test(newValues.pickupEmail)) {
                    errors.pickupEmail = emailInvalid;
                }

                return errors;
            }}
        >
            {({ values, setFieldValue, setSubmitting, errors, submitCount, isValidating }) => {
                if (isLoading) {
                    return <Spinner />;
                }
                else {
                    return (
                        <>
                            <GMDialogYesNo
                                ariaLabel={Localization.ConsentToTextMessages}
                                title={Localization.ConsentToTextMessages}
                                open={showOptIn}
                                yesText={Localization.Confirm}
                                noText={Localization.Cancel}
                                onYes={() => {
                                    upsertTextingOptIn(values.key, values.pickupPhone!, true, dealerKey)
                                        .finally(() => {
                                            const newEmail: string = values.pickupEmail!.trim();
                                            const newPhone: string = values.pickupPhone!.trim();
                                            setAppointment({
                                                ...appointment!,
                                                ...values,
                                                pickupEmail: newEmail,
                                                pickupPhone: newPhone,
                                                pickupContactPreference: values.pickupContactPreference ?? contactPreference,
                                            }, true, false, false, true, undefined, false, true, false, false, false, true)
                                                .finally(() => {
                                                    onProceed();
                                                });
                                        });
                                }}
                                onNo={() => {
                                    setFieldValue(nameof<typeof initialValues>('pickupContactPreference'), initialValues?.pickupContactPreference);
                                    setShowOptIn(false);
                                    setSubmitting(false);
                                }}
                            >
                                {
                                    Localization.formatString(
                                        Localization.TextingOptInAuthorization,
                                        (accountType === AccountType.business ? businessName : firstName) ?? lastName, environment as string,
                                    )
                                }
                                <LegalDocumentLinks />
                            </GMDialogYesNo>
                            <GMForm
                                topDivider
                                heading={
                                    <KeyManagementHeader
                                        secondaryHeading={
                                            Localization.formatString(
                                                Localization.HowDoesCustomerWantNotifications,
                                                (accountType === AccountType.business ? businessName : firstName) ?? lastName,
                                            )
                                        }
                                        leftAlignSecondaryHeading
                                        addKey
                                    />
                                }
                                submitButton={
                                    <CTAButton
                                        styleType={'primary'}
                                        type={'submit'}
                                        title={Localization.ClickToProceedToNextStep}
                                        label={Localization.Proceed}
                                        id={Localization.ClickToProceedToNextStep}
                                        ariaLabel={Localization.ClickToProceedToNextStep}
                                        disabled={isValidating}
                                    />
                                }
                            >
                                {customerTextingActive ? (
                                    <GMGrid
                                        id={'admin-contact-button-form-container'}
                                        container
                                        rowGap={1}
                                        className={css.contactButtonList}
                                        paddingBottom={errors.pickupContactPreference && submitCount > 0 ? '1vmin' : '2vmin'}
                                    >
                                        <GMGrid
                                            id={'admin-contact-button-container'}
                                            container
                                            xs={12}
                                            gap={3}
                                            className={css.contactButtons}
                                        >
                                            <GMGrid id={'admin-email-button-grid'} xs={4}>
                                                <GMContactPreferenceButton
                                                    name={nameof<AdminContactFormType>('pickupContactPreference')}
                                                    contactPreference={ContactPreferenceEnum.Email}
                                                    buttonSizeClassName={css.contactButton}
                                                    noSubmit
                                                    smallButton
                                                />
                                            </GMGrid>
                                            <GMGrid id={'admin-text-button-grid'} xs={4}>
                                                <GMContactPreferenceButton
                                                    name={nameof<AdminContactFormType>('pickupContactPreference')}
                                                    contactPreference={ContactPreferenceEnum.Text}
                                                    buttonSizeClassName={css.contactButton}
                                                    noSubmit
                                                    smallButton
                                                />
                                            </GMGrid>
                                            <GMGrid id={'admin-both-button-grid'} xs={4}>
                                                <GMContactPreferenceButton
                                                    name={nameof<AdminContactFormType>('pickupContactPreference')}
                                                    contactPreference={ContactPreferenceEnum.Both}
                                                    buttonSizeClassName={css.contactButton}
                                                    noSubmit
                                                    smallButton
                                                />
                                            </GMGrid>
                                        </GMGrid>
                                        {errors.pickupContactPreference && submitCount > 0 ? (
                                            <GMGrid
                                                id={'admin-contact-button-error-container'}
                                                container
                                                xs={12}
                                                className={css.error}
                                            >
                                                <GMGrid id={'admin-contact-button-error-grid'} xs={12}>
                                                    <GMErrorMessage error>
                                                        {errors.pickupContactPreference}
                                                    </GMErrorMessage>
                                                </GMGrid>
                                            </GMGrid>
                                        ) : null}
                                    </GMGrid>
                                ) : null}
                                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                                {customerTextingActive ? (
                                    <GMFormPhoneField
                                        name={nameof<typeof initialValues>('pickupPhone')}
                                        id={nameof<typeof initialValues>('pickupPhone')}
                                        title={Localization.TextingNumber}
                                        label={Localization.TextingNumber}
                                        ariaLabel={Localization.TextingNumber}
                                        required={values.pickupContactPreference === 'text' || values.pickupContactPreference === 'both'}
                                        noValidation
                                    />
                                ) : null}
                                <GMFormEmailField
                                    name={nameof<typeof initialValues>('pickupEmail')}
                                    id={nameof<typeof initialValues>('pickupEmail')}
                                    title={Localization.Email}
                                    label={Localization.Email}
                                    ariaLabel={Localization.Email}
                                    keyboardLayout={KeyboardLayouts.Email}
                                    required={!customerTextingActive || values.pickupContactPreference === 'email' || values.pickupContactPreference === 'both'}
                                    noValidation
                                />
                                {alternateLanguage !== Locales.none ? (
                                    <GMFormWrapper>
                                        <div className={css.checkboxField}>
                                            <GMFormCheckbox
                                                label={
                                                    Localization.formatString(
                                                        Localization.SendNotificationsInLanguage,
                                                        Localization.getString(LocalesFull[alternateLanguage as Locales]),
                                                    ) as string
                                                }
                                                labelVariant={'heading'}
                                                extraLabelWidth={kioskLocale === 'fr-ca'}
                                                checked={values.locale === alternateLanguage}
                                                onChange={() => {
                                                    setFieldValue(
                                                        nameof<typeof initialValues>('locale'),
                                                        values.locale !== alternateLanguage ? alternateLanguage : primaryLanguage,
                                                    );
                                                }}
                                            />
                                        </div>
                                    </GMFormWrapper>
                                ) : null}
                            </GMForm >
                        </>
                    );
                }
            }}
        </Formik >
    );
};